import React, { useMemo } from "react";
import { buildOrderbookDisplayRows, decNum, fmtTradeTimeShort } from "./optionsDataHelpers";

const toVisibleOrderbookRows = (levels, minRows) => {
  const display = buildOrderbookDisplayRows(levels.slice(0, 50));
  if (display.length >= minRows) return display;
  return [...display, ...Array.from({ length: minRows - display.length }, () => null)];
};

const fmt = (num, dec = 2) =>
  Number(num).toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec });

const fmtOptPrice = (v, dec = 2) => {
  const n = decNum(v);
  if (!Number.isFinite(n) || n <= 0) return "--";
  return fmt(n, dec);
};

const fmtIvPct = (v) => {
  const n = decNum(v);
  if (!Number.isFinite(n) || n <= 0) return "--";
  return `${fmt(n, 2)}%`;
};

export default function OptionsOrderbook({
  obView,
  setObView,
  askRows,
  bidRows,
  lastPrice,
  ivPct,
  recentTrades,
  assetLabel = "BTC",
}) {
  const minRows = obView === "both" ? 5 : 10;
  const paddedAskDisplay = useMemo(() => toVisibleOrderbookRows(askRows, minRows), [askRows, minRows]);
  const paddedBidDisplay = useMemo(() => toVisibleOrderbookRows(bidRows, minRows), [bidRows, minRows]);
  const maxRows = Math.max(paddedAskDisplay.length, paddedBidDisplay.length, minRows);

  const totalBid = bidRows.reduce((s, r) => s + decNum(r.qty), 0);
  const totalAsk = askRows.reduce((s, r) => s + decNum(r.qty), 0);
  const totalQty = totalBid + totalAsk;
  const bidPct = totalQty > 0 ? (totalBid / totalQty) * 100 : 0;
  const askPct = totalQty > 0 ? (totalAsk / totalQty) * 100 : 100 - bidPct;

  const tradeRows = useMemo(
    () => (Array.isArray(recentTrades) ? recentTrades.slice(0, 12) : []),
    [recentTrades]
  );

  const renderEmptyRows = (count, keyPrefix) =>
    Array.from({ length: count }, (_, i) => (
      <tr key={`${keyPrefix}-empty-${i}`}>
        <td className="agcex-text-left">--</td>
        <td>--</td>
        <td className="agcex-text-right">--</td>
      </tr>
    ));

  return (
    <div className="agcex-opt-orderbook">
      <div className="agcex-opt-ob-filters">
        <div
          className={`agcex-opt-ob-filter-icon ${obView === "both" ? "active" : ""}`}
          onClick={() => setObView("both")}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect x="0" y="0" width="6" height="6" fill="#0ecb81" />
            <rect x="0" y="8" width="6" height="6" fill="#f6465d" />
            <rect x="8" y="0" width="6" height="14" fill="#848e9c" />
          </svg>
        </div>
        <div
          className={`agcex-opt-ob-filter-icon ${obView === "sidebyside" ? "active" : ""}`}
          onClick={() => setObView("sidebyside")}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect x="0" y="0" width="6" height="14" fill="#f6465d" />
            <rect x="8" y="0" width="6" height="14" fill="#0ecb81" />
          </svg>
        </div>
        <div
          className={`agcex-opt-ob-filter-icon ${obView === "bids" ? "active" : ""}`}
          onClick={() => setObView("bids")}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect x="0" y="0" width="6" height="14" fill="#0ecb81" />
            <rect x="8" y="0" width="6" height="14" fill="#848e9c" />
          </svg>
        </div>
        <div
          className={`agcex-opt-ob-filter-icon ${obView === "asks" ? "active" : ""}`}
          onClick={() => setObView("asks")}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect x="0" y="0" width="6" height="14" fill="#f6465d" />
            <rect x="8" y="0" width="6" height="14" fill="#848e9c" />
          </svg>
        </div>
      </div>

      <div className="agcex-opt-ob-wrapper">
        {obView === "sidebyside" && (
          <>
            <table className="agcex-opt-ob-table agcex-opt-w-100">
              <thead>
                <tr>
                  <th className="agcex-text-left">Amt.({assetLabel})</th>
                  <th colSpan="2" className="agcex-text-center">Price</th>
                  <th className="agcex-text-right">Amt.({assetLabel})</th>
                </tr>
              </thead>
            </table>
            <div className="agcex-opt-ob-scroll-250">
              <table className="agcex-opt-ob-table agcex-opt-w-100">
                <tbody>
                  {Array.from({ length: maxRows }).map((_, i) => {
                    const ask = paddedAskDisplay[i];
                    const bid = paddedBidDisplay[i];
                    return (
                      <tr key={`ob-${i}`}>
                        <td className="agcex-text-left agcex-opt-w-25">{bid ? fmt(bid.qty, 4) : "--"}</td>
                        <td className="agcex-opt-ob-price-green agcex-text-right agcex-opt-w-25">{bid ? fmt(bid.price, 2) : "--"}</td>
                        <td className={`agcex-opt-ob-price-red agcex-text-left agcex-opt-w-25${ask ? " agcex-bg-red-light" : ""}`}>{ask ? fmt(ask.price, 2) : "--"}</td>
                        <td className="agcex-text-right agcex-opt-w-25">{ask ? fmt(ask.qty, 4) : "--"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="agcex-opt-ob-mid" style={{ justifyContent: "flex-end" }}>
              {lastPrice != null ? (
                <div style={{ textAlign: "right", paddingRight: "4px" }}>
                  <div className="agcex-opt-ob-mid-price">{fmtOptPrice(lastPrice, 2)}</div>
                  <div className="agcex-opt-ob-mid-iv">IV:{fmtIvPct(ivPct)}</div>
                </div>
              ) : (
                <div className="agcex-opt-ob-mid-empty">--</div>
              )}
            </div>
          </>
        )}

        {obView !== "sidebyside" && (
          <>
            <table className="agcex-opt-ob-table">
              <thead>
                <tr>
                  <th className="agcex-text-left">Price</th>
                  <th className="agcex-text-right">Amt.({assetLabel})</th>
                  <th className="agcex-text-right">Sum({assetLabel})</th>
                </tr>
              </thead>
            </table>

            {(obView === "both" || obView === "asks") && (
              <div className={obView === "both" ? "agcex-opt-ob-scroll-125" : "agcex-opt-ob-scroll-250"}>
                <table className="agcex-opt-ob-table">
                  <tbody>
                    {paddedAskDisplay.map((row, i) =>
                      row ? (
                        <tr key={`ask-${i}`}>
                          <td className="agcex-opt-ob-price-red agcex-text-left">{fmt(row.price, 2)}</td>
                          <td style={{ background: row.depth > 0 ? `linear-gradient(to left, rgba(246, 70, 93, 0.1) ${row.depth}%, transparent ${row.depth}%)` : "none" }}>{fmt(row.qty, 4)}</td>
                          <td className={row.depth > 0 ? "agcex-bg-red-light" : ""}>{fmt(row.sum, 4)}</td>
                        </tr>
                      ) : (
                        <tr key={`ask-empty-${i}`}>
                          <td className="agcex-text-left">--</td>
                          <td>--</td>
                          <td className="agcex-text-right">--</td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {(obView === "both" || obView === "bids" || obView === "asks") && (
              <div className="agcex-opt-ob-mid">
                {lastPrice != null ? (
                  <div className="agcex-text-right">
                    <div className="agcex-opt-ob-mid-price">{fmtOptPrice(lastPrice, 2)}</div>
                    <div className="agcex-opt-ob-mid-iv">IV:{fmtIvPct(ivPct)}</div>
                  </div>
                ) : (
                  <div className="agcex-opt-ob-mid-empty">--</div>
                )}
              </div>
            )}

            {(obView === "both" || obView === "bids") && (
              <div className={obView === "both" ? "agcex-opt-ob-scroll-125" : "agcex-opt-ob-scroll-250"}>
                <table className="agcex-opt-ob-table">
                  <tbody>
                    {paddedBidDisplay.map((row, i) =>
                      row ? (
                        <tr key={`bid-${i}`}>
                          <td className="agcex-opt-ob-price-green agcex-text-left">{fmt(row.price, 2)}</td>
                          <td style={{ background: row.depth > 0 ? `linear-gradient(to left, rgba(14, 203, 129, 0.1) ${row.depth}%, transparent ${row.depth}%)` : "none" }}>{fmt(row.qty, 4)}</td>
                          <td className={row.depth > 0 ? "agcex-bg-green-light" : ""}>{fmt(row.sum, 4)}</td>
                        </tr>
                      ) : (
                        <tr key={`bid-empty-${i}`}>
                          <td className="agcex-text-left">--</td>
                          <td>--</td>
                          <td className="agcex-text-right">--</td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <div className="agcex-opt-ob-bar-wrap" style={{ marginTop: "12px" }}>
        <div
          className={`agcex-opt-ob-bar-green ${obView === "asks" || obView === "sidebyside" ? "agcex-opt-ob-bar-flex-0" : "agcex-opt-ob-bar-flex-1"}`}
          style={totalQty > 0 ? { flex: bidPct } : undefined}
        />
        <div className="agcex-opt-ob-bar-divider" style={{ display: obView === "bids" || obView === "asks" || obView === "sidebyside" ? "none" : "block" }} />
        <div
          className={`agcex-opt-ob-bar-red ${obView === "bids" ? "agcex-opt-ob-bar-flex-0" : "agcex-opt-ob-bar-flex-1"}`}
          style={totalQty > 0 ? { flex: askPct } : undefined}
        />
      </div>
      <div className="agcex-opt-ob-stats">
        <span style={{ color: "#0ecb81" }}>B {totalQty > 0 ? `${fmt(bidPct, 2)}%` : "--"}</span>
        <span style={{ color: "#f6465d" }}>{totalQty > 0 ? `${fmt(askPct, 2)}%` : "--"} S</span>
      </div>

      <div className="agcex-opt-trades-container">
        <div className="agcex-opt-trades-header">Trades</div>
        <table className="agcex-opt-ob-table">
          <thead>
            <tr>
              <th className="agcex-text-left">Price</th>
              <th className="agcex-text-right">Amt.({assetLabel})</th>
              <th className="agcex-text-right">Time</th>
            </tr>
          </thead>
        </table>
        <div className="agcex-opt-trades-scroll">
          <table className="agcex-opt-ob-table">
            <tbody>
              {tradeRows.length === 0 ? (
                renderEmptyRows(3, "trade")
              ) : (
                tradeRows.map((t) => {
                  const side = String(t.side || "").toUpperCase();
                  const isBuy = side === "BUY";
                  return (
                    <tr key={t.trade_id || t._id || `${t.timestamp}-${t.price}`}>
                      <td className={`agcex-text-left ${isBuy ? "agcex-opt-ob-price-green" : "agcex-opt-ob-price-red"}`}>
                        {fmtOptPrice(t.price, 2)}
                      </td>
                      <td className="agcex-text-right">{fmt(decNum(t.quantity), 4)}</td>
                      <td className="agcex-text-right">{fmtTradeTimeShort(t.timestamp)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
